/**
 * Customer Webtools Dashboard - Main Dashboard Page
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { Box, Typography, Grid, Card, CardContent, Alert, Skeleton, Fade } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useDataFreshness } from '@/hooks/customer';
import { DataFreshnessWidget, DashboardSkeleton } from '@/components/customer';

function CustomerWebtoolsDashboard() {
  const { data: freshnessData, isLoading: freshnessLoading, error: freshnessError } = useDataFreshness({
    refetchInterval: 60000,
  });

  if (freshnessLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <Fade in timeout={300}>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Webtools Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor your search performance, analytics, and data sync status.
          </Typography>
        </Box>

        {/* Error Alert */}
        {freshnessError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load data freshness: {freshnessError.message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Data Freshness Widget */}
          <Grid item xs={12} md={4}>
            {freshnessLoading ? (
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            ) : freshnessData ? (
              <DataFreshnessWidget data={freshnessData} />
            ) : null}
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/performance"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Search Performance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View clicks, impressions, CTR, and position data from GSC and Bing.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/analytics"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sessions, users, engagement, and traffic sources from GA4.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/index-status"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Index Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      URL indexing coverage and crawl errors.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/local-business"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Local Business
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reviews, ratings, and Q&A from Google Business Profile.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/url-coverage"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      URL Coverage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monitor URL indexing status across search engines.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/sitemaps"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Sitemaps
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage sitemap submissions to search engines.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  component={RouterLink}
                  to="/customer/content-analysis"
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Content Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Content quality metrics, sentiment analysis, and recommendations.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
}

export default CustomerWebtoolsDashboard;
